import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useDisclosure,
  IconButton,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { SearchIcon, AddIcon, EditIcon, DeleteIcon } from "@chakra-ui/icons";
import Card from "components/Card/Card.js";
import { citiesService } from "api";

export default function Cities() {
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const tableRowColor = useColorModeValue("#F7FAFC", "navy.900");
  const textTableColor = useColorModeValue("gray.500", "white");
  
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [editingCity, setEditingCity] = useState(null);
  const [deletingCity, setDeletingCity] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  
  const toast = useToast();

  // Fetch cities on component mount
  useEffect(() => {
    fetchCities();
  }, []);

  // Filter cities when search term changes
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredCities(cities);
    } else {
      const filtered = cities.filter(city => 
        city.ar_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        city.en_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCities(filtered);
    }
  }, [searchTerm, cities]);

  // Debug validation errors state changes
  useEffect(() => {
    console.log('Validation errors state changed:', validationErrors);
  }, [validationErrors]);

  const fetchCities = async () => {
    setIsLoading(true);
    try {
      const response = await citiesService.getCities();
      console.log('Cities API response:', response);
      if (response.success) {
        // Handle both nested data structure and direct data
        const citiesData = response.data?.data || response.data || [];
        console.log('Cities data:', citiesData);
        setCities(citiesData);
        setFilteredCities(citiesData);
      } else {
        toast({
          title: "خطأ",
          description: response.message || "حدث خطأ أثناء جلب المدن",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ غير متوقع",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateCity = async (cityData) => {
    setActionLoading({ create: true });
    setValidationErrors({});
    try {
      const response = await citiesService.createCity(cityData);
      console.log('Create city response:', response);
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);
      console.log('Response success:', response.success);
      
      if (response.success) {
        toast({
          title: "نجح",
          description: "تم إنشاء المدينة بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onModalClose();
        fetchCities();
      } else {
        // Handle validation errors (422)
        if (response.status === 422 && response.data?.data?.error_messages) {
          console.log('Setting validation errors:', response.data.data.error_messages);
          console.log('Error messages structure:', response.data.data.error_messages);
          setValidationErrors(response.data.data.error_messages);
          console.log('Validation errors state after set:', response.data.data.error_messages);
          // Removed toast notification for validation errors
        } else {
          console.log('Not a validation error, showing general error');
          toast({
            title: "خطأ",
            description: response.message || "حدث خطأ أثناء إنشاء المدينة",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error in handleCreateCity:', error);
      toast({
        title: "خطأ",
        description: "حدث خطأ غير متوقع",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading({ create: false });
    }
  };

  const handleUpdateCity = async (id, cityData) => {
    setActionLoading({ update: id });
    setValidationErrors({});
    try {
      const response = await citiesService.updateCity(id, cityData);
      if (response.success) {
        toast({
          title: "نجح",
          description: "تم تحديث المدينة بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onModalClose();
        fetchCities();
      } else {
        // Handle validation errors (422)
        if (response.status === 422 && response.data?.data?.error_messages) {
          setValidationErrors(response.data.data.error_messages);
          // Removed toast notification for validation errors
        } else {
          toast({
            title: "خطأ",
            description: response.message || "حدث خطأ أثناء تحديث المدينة",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      toast({
        title: "خطأ",
        description: "حدث خطأ غير متوقع",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading({ update: false });
    }
  };

  const handleDeleteCity = async (id) => {
    setActionLoading({ delete: id });
    try {
      const response = await citiesService.deleteCity(id);
      if (response.success) {
        toast({
          title: "نجح",
          description: "تم حذف المدينة بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onDeleteClose();
        fetchCities();
      } else {
        toast({
          title: "خطأ",
          description: response.message || "حدث خطأ أثناء حذف المدينة",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "خطأ",
        description: "حدث خطأ غير متوقع",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActionLoading({ delete: false });
    }
  };

  const openCreateModal = () => {
    setEditingCity(null);
    setValidationErrors({});
    onModalOpen();
  };

  const openEditModal = (city) => {
    setEditingCity(city);
    setValidationErrors({});
    onModalOpen();
  };

  const handleModalClose = () => {
    setValidationErrors({});
    onModalClose();
  };

  const openDeleteDialog = (city) => {
    setDeletingCity(city);
    onDeleteOpen();
  };

  return (
    <Flex flexDirection='column' pt={{ base: "120px", md: "75px" }}>
      <Card>
        <Flex direction="column" p="20px">
          <Text fontSize="xl" fontWeight="bold" color={textColor} mb="20px">
            إدارة المدن
          </Text>
          
          {/* Search and Add Section */}
          <Flex direction={{ base: "column", md: "row" }} gap="20px" mb="20px">
            <InputGroup maxW="400px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="البحث في المدن..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg="white"
                border="1px solid"
                borderColor={borderColor}
              />
            </InputGroup>
            
            <Button
              leftIcon={<AddIcon />}
              colorScheme="blue"
              onClick={openCreateModal}
              minW="150px"
            >
              إضافة مدينة جديدة
            </Button>
            
            <Button
              variant="outline"
              onClick={fetchCities}
              isLoading={isLoading}
              minW="120px"
            >
              تحديث
            </Button>
          </Flex>

          {/* Cities Table */}
          <Box overflowX="auto">
            {isLoading ? (
              <Flex justify="center" align="center" py="40px">
                <Text color="gray.500">جاري تحميل المدن...</Text>
              </Flex>
            ) : filteredCities.length === 0 ? (
              <Flex justify="center" align="center" py="40px">
                <Text color="gray.500">
                  {searchTerm ? 'لا توجد نتائج للبحث' : 'لا توجد مدن'}
                </Text>
              </Flex>
            ) : (
              <Table variant="simple">
              <Thead>
                <Tr bg={tableRowColor}>
                  <Th color="gray.400" borderColor={borderColor} textAlign="center">
                    الاسم العربي
                  </Th>
                  <Th color="gray.400" borderColor={borderColor} textAlign="center">
                    الاسم الإنجليزي
                  </Th>
                  <Th color="gray.400" borderColor={borderColor} textAlign="center">
                    الإجراءات
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredCities.map((city) => (
                  <Tr key={city.id}>
                    <Td
                      color={textTableColor}
                      fontSize="sm"
                      fontWeight="bold"
                      borderColor={borderColor}
                      textAlign="center"
                    >
                      {city.ar_name}
                    </Td>
                    <Td
                      color={textTableColor}
                      fontSize="sm"
                      borderColor={borderColor}
                      textAlign="center"
                    >
                      {city.en_name}
                    </Td>
                    <Td borderColor={borderColor} textAlign="center">
                      <Flex gap="10px" justify="center">
                        <Tooltip label="تعديل">
                          <IconButton
                            icon={<EditIcon />}
                            colorScheme="blue"
                            size="sm"
                            onClick={() => openEditModal(city)}
                            aria-label="تعديل"
                            isLoading={actionLoading.update === city.id}
                            loadingText="جاري التحديث..."
                          />
                        </Tooltip>
                        <Tooltip label="حذف">
                          <IconButton
                            icon={<DeleteIcon />}
                            colorScheme="red"
                            size="sm"
                            onClick={() => openDeleteDialog(city)}
                            aria-label="حذف"
                            isLoading={actionLoading.delete === city.id}
                            loadingText="جاري الحذف..."
                          />
                        </Tooltip>
                      </Flex>
                    </Td>
                  </Tr>
                ))}
                              </Tbody>
              </Table>
            )}
            </Box>
        </Flex>
      </Card>

             {/* Create/Edit Modal */}
               <CityModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          city={editingCity}
          onSubmit={editingCity ? handleUpdateCity : handleCreateCity}
          actionLoading={actionLoading}
          validationErrors={validationErrors}
        />

      {/* Delete Confirmation Dialog */}
      <AlertDialog isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
        <AlertDialogOverlay bg="blackAlpha.300" backdropFilter="blur(10px)">
          <AlertDialogContent
          mx="20px"
          maxW="500px"
          zIndex={9999}
          position="relative"
          borderRadius="15px"
          boxShadow="0 20px 60px rgba(0, 0, 0, 0.3)"
          border="1px solid"
          borderColor="gray.200"
        >
            <AlertDialogHeader
              fontSize="xl"
              fontWeight="bold"
              textAlign="center"
              borderBottom="1px solid"
              borderColor="gray.200"
              pb="20px"
            >
              حذف المدينة
            </AlertDialogHeader>
            <AlertDialogBody
              pt="20px"
              pb="20px"
              textAlign="center"
              fontSize="md"
              color="gray.600"
            >
              هل أنت متأكد من حذف المدينة <strong>"{deletingCity?.ar_name}"</strong>؟ لا يمكن التراجع عن هذا الإجراء.
            </AlertDialogBody>
            <AlertDialogFooter
              borderTop="1px solid"
              borderColor="gray.200"
              pt="20px"
              gap="15px"
            >
              <Button
                onClick={onDeleteClose}
                size="lg"
                minW="120px"
                borderRadius="8px"
                variant="outline"
                border="2px solid"
                borderColor="gray.300"
                _hover={{ bg: "gray.50", borderColor: "gray.400" }}
              >
                إلغاء
              </Button>
              <Button
                colorScheme="red"
                onClick={() => handleDeleteCity(deletingCity?.id)}
                size="lg"
                minW="120px"
                borderRadius="8px"
                _hover={{ transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)" }}
              >
                حذف
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Flex>
  );
}

// City Modal Component
function CityModal({ isOpen, onClose, city, onSubmit, actionLoading, validationErrors }) {
  const [formData, setFormData] = useState({
    ar_name: "",
    en_name: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Debug validation errors
  console.log('CityModal validationErrors:', validationErrors);

  useEffect(() => {
    if (city) {
      setFormData({
        ar_name: city.ar_name || "",
        en_name: city.en_name || ""
      });
    } else {
      setFormData({
        ar_name: "",
        en_name: ""
      });
    }
  }, [city]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.ar_name.trim() || !formData.en_name.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (city) {
        await onSubmit(city.id, formData);
      } else {
        await onSubmit(formData);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent
        mx="20px"
        maxW="500px"
        zIndex={9999}
        position="relative"
        borderRadius="15px"
        boxShadow="0 20px 60px rgba(0, 0, 0, 0.3)"
        border="1px solid"
        borderColor="gray.200"
      >
        <form onSubmit={handleSubmit}>
          <ModalHeader
            borderBottom="1px solid"
            borderColor="gray.200"
            pb="20px"
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
          >
            {city ? "تعديل المدينة" : "إضافة مدينة جديدة"}
          </ModalHeader>
          <ModalCloseButton
            top="20px"
            right="20px"
            size="lg"
            borderRadius="50%"
            bg="gray.100"
            _hover={{ bg: "gray.200" }}
          />
          <ModalBody pt="20px" pb="30px">
                         <FormControl mb="20px" isInvalid={!!validationErrors.ar_name}>
               <FormLabel fontWeight="600" mb="8px" color="gray.700">
                 الاسم العربي
               </FormLabel>
               <Input
                 value={formData.ar_name}
                 onChange={(e) => setFormData({ ...formData, ar_name: e.target.value })}
                 placeholder="أدخل الاسم العربي"
                 required
                 size="lg"
                 borderRadius="8px"
                 border="2px solid"
                 borderColor={validationErrors.ar_name ? "red.500" : "gray.200"}
                 _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                 _hover={{ borderColor: validationErrors.ar_name ? "red.400" : "gray.300" }}
               />
                               <FormErrorMessage textAlign="right">
                  {validationErrors.ar_name?.[0]}
                </FormErrorMessage>
             </FormControl>
                         <FormControl isInvalid={!!validationErrors.en_name}>
               <FormLabel fontWeight="600" mb="8px" color="gray.700">
                 الاسم الإنجليزي
               </FormLabel>
               <Input
                 value={formData.en_name}
                 onChange={(e) => setFormData({ ...formData, en_name: e.target.value })}
                 placeholder="أدخل الاسم الإنجليزي"
                 required
                 size="lg"
                 borderRadius="8px"
                 border="2px solid"
                 borderColor={validationErrors.en_name ? "red.500" : "gray.200"}
                 _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                 _hover={{ borderColor: validationErrors.en_name ? "red.400" : "gray.300" }}
               />
                               <FormErrorMessage textAlign="right">
                  {validationErrors.en_name?.[0]}
                </FormErrorMessage>
             </FormControl>
          </ModalBody>
          <ModalFooter
            borderTop="1px solid"
            borderColor="gray.200"
            pt="20px"
            gap="15px"
          >
            <Button
              variant="outline"
              onClick={onClose}
              size="lg"
              minW="120px"
              borderRadius="8px"
              border="2px solid"
              borderColor="gray.300"
              _hover={{ bg: "gray.50", borderColor: "gray.400" }}
            >
              إلغاء
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              size="lg"
              minW="120px"
              borderRadius="8px"
              isLoading={isSubmitting || actionLoading.create || actionLoading.update}
              loadingText={city ? "جاري التحديث..." : "جاري الإنشاء..."}
              _hover={{ transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)" }}
            >
              {city ? "تحديث" : "إنشاء"}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
